import React, { useState, useEffect, useMemo } from "react";
import {
    Autocomplete,
    TextField,
    Box,
    CircularProgress,
    createFilterOptions,
    Card,
    CardContent,
    Typography,
    Button,
    Stack,
    InputAdornment,
    useTheme,
    useMediaQuery,
    Select,
    MenuItem
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import InventoryIcon from "@mui/icons-material/Inventory";
import DescriptionIcon from "@mui/icons-material/Description";
import CategoryIcon from "@mui/icons-material/Category";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";

import { BOTON_PRIMARIO_SX, BOTON_SECUNDARIO_SX } from "../../../../components/ui/buttonStyles";
import PeticionesService from "../../api/peticiones.api";
import ProductService from "../../../../services/product.service";
import { PETICION_TIPO } from "../../constants";

const UNIDADES_MINIMAS = 1;
const DEBOUNCE_MS = 250;
const BUSQUEDA_MIN_CARACTERES = 2;

// Helper: obtener todos los codigos de barras de un producto
const getAllBarcodes = (producto) => producto?.BarrasModels?.map((b) => b.BARRAS).filter(Boolean) ?? [];

// Formulario de creación de petición con búsqueda de producto y validaciones
const PeticionForm = ({ onSave, onError, onCancel }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });

    // Estado del tipo de petición
    const [tipoPeticion, setTipoPeticion] = useState(PETICION_TIPO.PETICION.value);

    // Estado del buscador de productos (patron debounce de TraspasoAlmacenes)
    const [q, setQ] = useState("");
    const [opciones, setOpciones] = useState([]);
    const [loadingSearch, setLoadingSearch] = useState(false);
    const [productoSeleccionado, setProductoSeleccionado] = useState(null);

    // Estado del campo unidades
    const [unidades, setUnidades] = useState(UNIDADES_MINIMAS);

    // Estado del campo número de albarán
    const [numeroAlbaran, setNumeroAlbaran] = useState("");

    // Estado de guardado en progreso (evita dobles clicks)
    const [guardando, setGuardando] = useState(false);

    // ---------- busqueda de producto con debounce de 250ms ----------
    // Patron reutilizado de TraspasoAlmacenes.js:
    // solo busca cuando q tiene al menos 2 caracteres, limpia con cleanup.
    // DECISION: El filtrado por codigo de barras es client-side usando filterOptions,
    // evitando llamadas adicionales al servidor.
    useEffect(() => {
        let alive = true;

        const buscarProductos = async () => {
            if (!q || q.trim().length < BUSQUEDA_MIN_CARACTERES) {
                setOpciones([]);
                return;
            }

            setLoadingSearch(true);
            try {
                const resultados = await ProductService.getProducts(q.trim());
                if (alive) setOpciones(resultados ?? []);
            } catch (error) {
                console.log(error.message);
                if (alive) setOpciones([]);
            } finally {
                if (alive) setLoadingSearch(false);
            }
        };

        const temporizador = setTimeout(buscarProductos, DEBOUNCE_MS);

        return () => {
            alive = false;
            clearTimeout(temporizador);
        };
    }, [q]);

    // ---------- auto-seleccion cuando solo hay 1 resultado ----------
    useEffect(() => {
        if (opciones.length === 1) {
            setProductoSeleccionado(opciones[0]);
            setQ(`${opciones[0].CODIGO} — ${opciones[0].NOMBRE}`);
        }
    }, [opciones]);

    // ---------- filterOptions: busqueda local por codigo, nombre y codigos de barras ----------
    const filterOptions = useMemo(
        () =>
            createFilterOptions({
                stringify: (opt) => {
                    const barcodes = getAllBarcodes(opt).join(" ");
                    return `${opt?.CODIGO ?? ""} ${opt?.NOMBRE ?? ""} ${barcodes}`;
                },
            }),
        []
    );

    const resetearFormulario = () => {
        setTipoPeticion(PETICION_TIPO.PETICION.value);
        setQ("");
        setOpciones([]);
        setProductoSeleccionado(null);
        setUnidades(UNIDADES_MINIMAS);
        setNumeroAlbaran("");
    };

    // Validaciones de negocio separadas de la logica de envio (facil de testear)
    const validarFormulario = () => {
        if (!productoSeleccionado) {
            return 'Selecciona un producto';
        }

        const unidadesNumericas = Number(unidades);
        if (!Number.isInteger(unidadesNumericas) || unidadesNumericas < UNIDADES_MINIMAS) {
            return 'Las unidades deben ser mayores que 0';
        }

        // El número de albarán es obligatorio solo si NO es reposición
        if (tipoPeticion !== PETICION_TIPO.REPOSICION.value && !numeroAlbaran.trim()) {
            return 'El número de albarán es obligatorio';
        }

        return null;
    };

    const handleGuardar = async () => {
        const mensajeError = validarFormulario();
        if (mensajeError) {
            if (onError) onError(mensajeError);
            return;
        }

        setGuardando(true);
        try {
            // Construir objeto con los datos de la petición
            // El servicio asigna el usuario_id y el estado internamente
            const datosPeticion = {
                referencia: productoSeleccionado.CODIGO,
                unidades: Number(unidades),
                tipo: tipoPeticion,
            };

            // Solo incluir numeroAlbaran si tiene valor (no es obligatorio para reposición)
            if (numeroAlbaran.trim()) {
                datosPeticion.numeroAlbaran = numeroAlbaran.trim();
            }

            const peticionCreada = await PeticionesService.create(datosPeticion);

            resetearFormulario();

            if (onSave) onSave(peticionCreada);
        } catch (error) {
            console.log(error.message);
            if (onError) onError(error.response?.data?.message || error.message);
        } finally {
            setGuardando(false);
        }
    };

    const handleCancelar = () => {
        if (onCancel) onCancel();
    };

    const handleCambioUnidades = (e) => {
        // Solo permitir enteros: parseamos el valor y descartamos decimales al escribir
        const valorEntero = parseInt(e.target.value, 10);
        setUnidades(isNaN(valorEntero) ? "" : valorEntero);
    };

    return (
        <Box sx={{ width: "100%" }}>
            <Card
                sx={{
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    borderRadius: 2,
                }}
            >
                <CardContent sx={{ p: 3 }}>
                    {/* Header */}
                    <Box sx={{ mb: 3 }}>
                        <Typography
                            variant={isMobile ? "h6" : "h5"}
                            sx={{
                                fontWeight: 700,
                                color: "#212529",
                                mb: 0.5,
                            }}
                        >
                            Nueva petición
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: "#6c757d",
                            }}
                        >
                            Solicita productos al almacén
                        </Typography>
                    </Box>

                    <Stack spacing={3}>
                        {/* Tipo de petición */}
                        <Box>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    mb: 1,
                                    color: "#495057",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                }}
                            >
                                <CategoryIcon sx={{ fontSize: 18 }} />
                                Tipo de Petición *
                            </Typography>
                            <Select
                                id="tipo-peticion"
                                fullWidth
                                value={tipoPeticion}
                                onChange={(e) => setTipoPeticion(e.target.value)}
                                sx={{
                                    backgroundColor: "#fff",
                                }}
                            >
                                <MenuItem value={PETICION_TIPO.PETICION.value}>
                                    {PETICION_TIPO.PETICION.label}
                                </MenuItem>
                                <MenuItem value={PETICION_TIPO.REPOSICION.value}>
                                    {PETICION_TIPO.REPOSICION.label}
                                </MenuItem>
                            </Select>
                        </Box>

                        {/* Búsqueda de producto */}
                        <Box>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    mb: 1,
                                    color: "#495057",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                }}
                            >
                                <SearchIcon sx={{ fontSize: 18 }} />
                                Producto *
                            </Typography>
                            <Autocomplete
                                id="busqueda-producto"
                                options={opciones}
                                value={productoSeleccionado}
                                loading={loadingSearch}
                                onChange={(_, valorSeleccionado) => setProductoSeleccionado(valorSeleccionado)}
                                inputValue={q}
                                onInputChange={(_, valorTexto) => setQ(valorTexto)}
                                filterOptions={filterOptions}
                                isOptionEqualToValue={(opcion, valor) => opcion?.CODIGO === valor?.CODIGO}
                                getOptionLabel={(opcion) => `${opcion?.CODIGO ?? ''} — ${opcion?.NOMBRE ?? ''}`}
                                renderOption={(props, opcion) => (
                                    <Box component="li" {...props}>
                                        <Box sx={{ width: "100%" }}>
                                            <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                                                {opcion.CODIGO}
                                            </Typography>
                                            <Typography sx={{ fontSize: 13, color: "#6c757d" }}>
                                                {opcion.NOMBRE}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Escribe código, nombre o código de barras..."
                                        variant="outlined"
                                        fullWidth
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon sx={{ color: "#6c757d" }} />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <>
                                                    {loadingSearch ? (
                                                        <CircularProgress color="inherit" size={20} />
                                                    ) : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                backgroundColor: "#fff",
                                            },
                                        }}
                                    />
                                )}
                                noOptionsText={
                                    q.trim().length < BUSQUEDA_MIN_CARACTERES
                                        ? 'Escribe al menos 2 caracteres para buscar'
                                        : 'Sin resultados'
                                }
                            />
                        </Box>

                        {/* Unidades y Número de Albarán */}
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={2}
                        >
                            {/* Unidades */}
                            <Box sx={{ flex: { xs: 1, sm: "0 0 35%" } }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 600,
                                        mb: 1,
                                        color: "#495057",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                    }}
                                >
                                    <InventoryIcon sx={{ fontSize: 18 }} />
                                    Unidades *
                                </Typography>
                                <TextField
                                    id="unidades"
                                    type="number"
                                    fullWidth
                                    variant="outlined"
                                    inputProps={{
                                        min: UNIDADES_MINIMAS,
                                        step: 1,
                                    }}
                                    value={unidades}
                                    onChange={handleCambioUnidades}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            backgroundColor: "#fff",
                                        },
                                    }}
                                />
                            </Box>

                            {/* Número de Albarán - Solo visible para tipo "Petición" */}
                            {tipoPeticion === PETICION_TIPO.PETICION.value && (
                                <Box sx={{ flex: 1 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 600,
                                            mb: 1,
                                            color: "#495057",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                        }}
                                    >
                                        <DescriptionIcon sx={{ fontSize: 18 }} />
                                        Número de Albarán *
                                    </Typography>
                                    <TextField
                                        id="numero-albaran"
                                        type="text"
                                        fullWidth
                                        variant="outlined"
                                        placeholder="Ej: 00-12345"
                                        value={numeroAlbaran}
                                        onChange={(e) => setNumeroAlbaran(e.target.value)}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                backgroundColor: "#fff",
                                            },
                                        }}
                                    />
                                </Box>
                            )}
                        </Stack>

                        {/* Botones de acción */}
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1.5}
                            sx={{ pt: 2 }}
                        >
                            <Button
                                variant="contained"
                                startIcon={guardando ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <SaveIcon />}
                                onClick={handleGuardar}
                                disabled={guardando}
                                fullWidth={isMobile}
                                sx={BOTON_PRIMARIO_SX}
                            >
                                {guardando ? 'Guardando...' : 'Guardar petición'}
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<CancelIcon />}
                                onClick={handleCancelar}
                                disabled={guardando}
                                fullWidth={isMobile}
                                sx={BOTON_SECUNDARIO_SX}
                            >
                                Cancelar
                            </Button>
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>
        </Box>
    );
};

export default PeticionForm;
